import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { errorMessage, API_BASE } from "../lib/api";
import { PageHeader, GhostButton, PillBadge, inputClass } from "../components/Primitives";
import LinkedEntityFilters from "../components/LinkedEntityFilters";
import { useFullscreenRoute } from "../components/StickyFiltersBar";
import { SortableHeader, useSortableRows } from "../components/SortableTable";
import OpenShiftEditor from "../components/OpenShiftEditor";
import { useAutoRefresh } from "../lib/useAutoRefresh";
import { Download, FileText, RefreshCw, AlertTriangle, Maximize2, Minimize2, Repeat, Calendar as CalendarIcon, ChevronDown, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { KioskIdleOverlay } from "../components/KioskIdleOverlay";
import { toast } from "sonner";

// SSE event types that should trigger an /open-shifts refresh. Kept at module scope
// so the reference is stable across renders and doesn't spin up new EventSources.
const REFRESH_ON_EVENTS = ["coverage_change", "shift_update", "incident"];

function isoDate(d) {
  // Local YYYY-MM-DD (browser timezone) — avoids UTC offset bugs on West-of-UTC.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
/** Default open-shifts window — today going forward for 7 days (1 week).
 *  iter329 — Default changed from 30d → 7d per user request. Ops rarely
 *  needs to see a full month by default; 1-week view is faster to scan. */
function defaultRange() {
  return forwardRange(7);
}

/** Build a date range starting today, `days` forward. */
function forwardRange(days) {
  const start = new Date();
  const end = new Date(start);
  end.setDate(end.getDate() + days);
  return { start: isoDate(start), end: isoDate(end) };
}

const COLUMNS = [
  { key: "date", label: "Date" },
  { key: "site_name", label: "Site" },
  { key: "client_name", label: "Client" },
  { key: "start_time", label: "Start", render: (r) => new Date(r.start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
  { key: "end_time", label: "End", render: (r) => new Date(r.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
  { key: "kind", label: "Kind", render: (r) => (
    <div className="inline-flex items-center gap-1.5">
      <PillBadge tone={r.kind === "open" ? "amber" : "default"}>{r.kind}</PillBadge>
      {r.recurrence_group && <Repeat className="size-3 text-zinc-500" title="Recurring" />}
    </div>
  ) },
  { key: "guards_assigned", label: "Assigned" },
  { key: "min_guards", label: "Min Required" },
];

export default function OpenShifts() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isFs = useFullscreenRoute(pathname);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState(defaultRange);
  // iter329 — Active period chip (1wk / 2wks / 1mo) + collapsible filter bar.
  const [periodDays, setPeriodDays] = useState(7);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const setPeriod = (d) => {
    setPeriodDays(d);
    setRange(forwardRange(d));
  };
  // Today (YYYY-MM-DD, local) — recomputed on each render, cheap and always fresh
  // enough for the "highlight today" UI cue and past-shift filtering.
  const today = isoDate(new Date());
  const [filters, setFilters] = useState({ client_names: [], site_ids: [], guard_ids: [] });
  const [editing, setEditing] = useState(null); // open-shift row to edit
  const isClient = user?.role === "client";
  const canEdit = user && ["admin", "super_admin", "operations_manager", "supervisor", "dispatcher", "chief_security_officer"].includes(user.role);

  const filterKindTotals = useMemo(() => {
    const totals = { open: 0, understaffed: 0, today_open: 0 };
    const t = isoDate(new Date());
    rows.forEach((r) => {
      if (r.kind === "open") totals.open++;
      else if (r.kind === "understaffed") totals.understaffed++;
      if ((r.date || "") === t) totals.today_open++;
    });
    return totals;
  }, [rows]);

  const params = useMemo(() => {
    const p = { start: range.start, end: range.end, active_only: true };
    if (filters.site_ids.length) p.site_id = filters.site_ids.join(",");
    if (filters.client_names.length) p.client_name = filters.client_names.join(",");
    return p;
  }, [range, filters.site_ids, filters.client_names]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/reports/open-shifts", { params });
      // Backend now hard-filters shifts whose end_time has passed (iter 207).
      // Belt-and-braces: also drop client-side any row whose end_time
      // slipped past `now` between fetch and render.
      const nowIso = new Date().toISOString();
      const rowsFwd = (res.data?.rows || []).filter((r) => (r.end_time || "") > nowIso);
      setRows(rowsFwd);
    } catch (e) { toast.error(errorMessage(e, "Failed to load")); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh: 60s polling + immediate refresh on any SSE event that could
  // change the open-shifts population (someone released, a shift got edited, etc).
  const { lastRefreshed, refreshNow } = useAutoRefresh(load, {
    intervalMs: 60_000,
    sseEvents: REFRESH_ON_EVENTS,
  });

  const sortAccessors = useMemo(() => ({
    date: (r) => r.date || "",
    site_name: (r) => (r.site_name || "").toLowerCase(),
    client_name: (r) => (r.client_name || "").toLowerCase(),
    start_time: (r) => r.start_time || "",
    end_time: (r) => r.end_time || "",
    kind: (r) => (r.kind || "").toLowerCase(),
    guards_assigned: (r) => Number(r.guards_assigned) || 0,
    min_guards: (r) => Number(r.min_guards) || 0,
  }), []);
  const sortState = useSortableRows(rows, sortAccessors, { defaultKey: "date", defaultDir: "asc", storageKey: "open-shifts" });

  const exportTo = async (fmt) => {
    try {
      const query = new URLSearchParams({ ...params, format: fmt }).toString();
      const url = `${API_BASE}/reports/open-shifts?${query}`;
      const win = window.open(url, "_blank", "noopener");
      if (!win) toast.error("Popup blocked — allow popups to download");
    } catch (e) { toast.error(errorMessage(e, "Export failed")); }
  };

  const onFullscreen = () => {
    if (isFs) window.close();
    else window.open("/open-shifts/fullscreen", "_blank", "noopener");
  };

  const containerClass = isFs ? "fixed inset-0 z-50 bg-[#0A0A0A] overflow-auto" : "";

  return (
    <div className={containerClass}>
      <KioskIdleOverlay enabled={isFs} label="Open · Understaffed" />
      {!isFs && (
        <PageHeader
          title="Open / Understaffed"
          subtitle="Shifts in the range with no  assigned or below the site's minimum officer count. Fix these before they turn into no-shows."
          action={
            <div className="flex items-center gap-2">
              <GhostButton data-testid="open-shifts-fullscreen" onClick={onFullscreen}>
                <Maximize2 className="size-3.5" /> Fullscreen
              </GhostButton>
              <GhostButton data-testid="open-shifts-refresh" onClick={refreshNow} title={`Auto-refresh every 60s · Last: ${lastRefreshed.toLocaleTimeString()}`}>
                <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                <span
                  data-testid="open-shifts-last-refreshed"
                  className="ml-1.5 text-[9px] text-zinc-500 font-mono normal-case tracking-normal"
                >
                  {lastRefreshed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
              </GhostButton>
              <GhostButton data-testid="open-shifts-csv" onClick={() => exportTo("csv")}>
                <Download className="size-3.5" /> CSV
              </GhostButton>
              <GhostButton data-testid="open-shifts-pdf" onClick={() => exportTo("pdf")}>
                <FileText className="size-3.5" /> PDF
              </GhostButton>
            </div>
          }
        />
      )}

      <div className={isFs ? "p-6" : "p-8"}>
      {isFs && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-mute">Roster / Coverage</div>
            <div className="font-display uppercase font-black text-2xl">Open · Understaffed</div>
          </div>
          <GhostButton data-testid="open-shifts-exit-fullscreen" onClick={onFullscreen}>
            <Minimize2 className="size-3.5" /> Close
          </GhostButton>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="border border-[#27272A] bg-[#0A0A0A] p-3" data-testid="open-shifts-kpi-total">
          <div className="label-mute">In Range</div>
          <div className="font-display text-2xl font-black">{rows.length}</div>
        </div>
        <div className="border border-[#27272A] bg-[#0A0A0A] p-3" data-testid="open-shifts-kpi-open">
          <div className="label-mute">Open (unassigned)</div>
          <div className="font-display text-2xl font-black text-[#FF3B30]">{filterKindTotals.open}</div>
        </div>
        <div className="border border-[#27272A] bg-[#0A0A0A] p-3" data-testid="open-shifts-kpi-understaffed">
          <div className="label-mute">Understaffed</div>
          <div className="font-display text-2xl font-black text-[#FF9F0A]">{filterKindTotals.understaffed}</div>
        </div>
        <div
          className={`border p-3 ${filterKindTotals.today_open > 0 ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-[#27272A] bg-[#0A0A0A]"}`}
          data-testid="open-shifts-kpi-today"
          title="Open or understaffed shifts starting TODAY — fix these before roll-call"
        >
          <div className="label-mute">Today (URGENT)</div>
          <div className={`font-display text-2xl font-black ${filterKindTotals.today_open > 0 ? "text-[#FF453A]" : "text-zinc-500"}`}>{filterKindTotals.today_open}</div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 md:grid-cols-1">
        <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
          <div className="label-mute">Window</div>
          <div className="font-mono text-xs text-zinc-300">{range.start} → {range.end}</div>
        </div>
      </div>

      <div className="border border-[#27272A] bg-[#121212] mb-4">
        {/* iter329 — Collapsible filter bar. Header row is always
            visible and shows the current period + active filters count
            at a glance. Body expands on click. */}
        <button
          type="button"
          data-testid="open-shifts-filters-toggle"
          onClick={() => setFiltersOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#0A0A0A] transition-colors"
        >
          <div className="flex items-center gap-3">
            {filtersOpen ? <ChevronDown className="size-3.5 text-zinc-400" /> : <ChevronRight className="size-3.5 text-zinc-400" />}
            <div className="label-mute">Filters</div>
            <div className="text-xs text-zinc-500 font-mono">
              {range.start} → {range.end}
              {(filters.site_ids.length + filters.client_names.length) > 0 && (
                <span className="ml-2 text-[#FF3B30]">
                  · {filters.site_ids.length + filters.client_names.length} active
                </span>
              )}
            </div>
          </div>
          {/* Quick period chips are always visible in the header for a
              one-click switch without expanding the panel. */}
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            {[
              { d: 7, label: "1 Wk" },
              { d: 14, label: "2 Wks" },
              { d: 30, label: "1 Mo" },
            ].map(({ d, label }) => {
              const on = periodDays === d;
              return (
                <button
                  key={d}
                  type="button"
                  data-testid={`open-shifts-period-${d}d`}
                  onClick={() => setPeriod(d)}
                  className={`text-[10px] uppercase tracking-widest border px-2.5 py-1 transition-colors ${
                    on
                      ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                      : "border-[#27272A] text-zinc-400 hover:border-[#FF3B30] hover:text-[#FF3B30]"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </button>
        {filtersOpen && (
          <div className="border-t border-[#27272A] p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="open-shifts-filters-body">
            <div>
              <div className="label-mute mb-1">Start date</div>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="open-shifts-start"
                    className={`${inputClass} inline-flex items-center justify-between gap-2 text-left`}
                  >
                    <span className="font-mono text-xs">{range.start}</span>
                    <CalendarIcon className="size-3.5 text-zinc-500" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-auto bg-[#0A0A0A] border-[#27272A]" align="start">
                  <Calendar
                    mode="single"
                    selected={new Date(`${range.start}T00:00:00`)}
                    onSelect={(d) => d && setRange((r) => ({ ...r, start: isoDate(d) }))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <div className="label-mute mb-1">End date</div>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="open-shifts-end"
                    className={`${inputClass} inline-flex items-center justify-between gap-2 text-left`}
                  >
                    <span className="font-mono text-xs">{range.end}</span>
                    <CalendarIcon className="size-3.5 text-zinc-500" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-auto bg-[#0A0A0A] border-[#27272A]" align="start">
                  <Calendar
                    mode="single"
                    selected={new Date(`${range.end}T00:00:00`)}
                    onSelect={(d) => d && setRange((r) => ({ ...r, end: isoDate(d) }))}
                    disabled={(d) => d < new Date(`${range.start}T00:00:00`)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="md:col-span-2">
              <LinkedEntityFilters
                testIdPrefix="open-shifts-filter"
                value={filters}
                onChange={setFilters}
                show={{ client: !isClient, site: true, guard: false }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="border border-[#27272A] bg-[#121212] overflow-x-auto" data-testid="open-shifts-table">
        {canEdit && rows.length > 0 && (
          <div className="px-3 py-2 border-b border-[#27272A] text-[10px] uppercase tracking-widest text-zinc-500">
            Tip · Click any row to edit guard assignment {" "}
            <span className="text-zinc-400">(includes recurring series option)</span>
          </div>
        )}
        {rows.length === 0 ? (
          <div className="text-center py-16 text-zinc-500 scanlines">
            {loading ? "Loading…" : (
              <div className="flex flex-col items-center gap-2">
                <AlertTriangle className="size-6 text-[#34C759]" />
                <div>Nothing open or understaffed in the range. 👍</div>
              </div>
            )}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="border-b border-[#27272A]">
              <tr>
                {COLUMNS.map((c) => (
                  <SortableHeader key={c.key} k={c.key} state={sortState} className="px-3 py-2">
                    {c.label}
                  </SortableHeader>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272A]">
              {sortState.sorted.map((r, i) => {
                const isToday = (r.date || "") === today;
                return (
                <tr
                  key={`${r.shift_id || i}`}
                  data-testid={`open-shifts-row-${r.shift_id || i}`}
                  data-today={isToday ? "1" : undefined}
                  onClick={() => canEdit && setEditing(r)}
                  className={`hover:bg-[#1A1A1A] ${canEdit ? "cursor-pointer" : ""} ${isToday ? "bg-[#FF3B30]/10 border-l-4 border-l-[#FF3B30] text-[#FF453A]" : ""}`}
                  title={canEdit ? (isToday ? "Open TODAY — click to edit guards" : "Click to edit guards on this shift") : undefined}
                >
                  {COLUMNS.map((c) => (
                    <td key={c.key} className={`px-3 py-2 align-top ${isToday && c.key === "date" ? "font-black" : ""}`}>
                      {c.render ? c.render(r) : (r[c.key] ?? "—")}
                    </td>
                  ))}
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      </div>

      {editing && (
        <OpenShiftEditor
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
